<template lang="pug">

main.modal-wrapper.bulk-edit
    svg-icon.close-modal.cursor(name='close' size="small" @click.native="closeModal()")

    template
        Panel(panelClassName="modal-information")

            sectionHeader(slot="header" :title="isDelete ? 'Delete your audiences' : 'Edit your audiences'" :counter="bulkList.length")

            .modal-information-content(slot="content")
                .bulk-edit-container
                    .bulk-edit-item(v-for="audience of bulkList" :key="audience.id")
                        div.l-flex-row
                            div.row-item-status(title="Status" :class="audience.status === 'inactive' ? 'item-inactive' : 'item-active'")
                            div.bulk-audience-id {{audience.id}}
                        div.bulk-audience-label {{audience.label}}
                        div.bulk-audience-bootstrap {{audience.bootstrap}}


        main.modal-main.modal__medium.bulk-edit-main.is-relative
            spinner.is-pending(v-if="isUpdatedOrDeletedLoading" width="50" height="50")
            header.modal-header
                sectionHeader(:title="isDelete ? 'Delete Audiences' : 'Edit audiences'")

            .modal-body.modal-content-audience(v-if="!isDelete")
                .form-subsection
                    el-select.capitalize-label(v-model="editedField")
                        el-option(
                            v-for="type in editableFields"
                            :key="type.value"
                            :label="type.label"
                            :value="type.value"
                        )

                //- End date
                div.l-flex-row.has-gutter_medium.end-date__block(v-if="editedField === 'endDate'")
                    div.form-subsection
                        div.form-label__required
                            label End Date
                        el-date-picker(
                            v-model="endDate"
                            name="end_date"
                            editable=true
                            clearable=false
                            class="datepicker"
                            type="date"
                            :picker-options="endDateOptions"
                            v-validate="'date_format:YYYY-MM-DD|required'"
                            placeholder="Audience end date")
                    // div.form-subsection.w-180(v-if="!hasFirstParty")
                        div.message-info
                            svg-icon(name='circleInfo' size="large")
                            div.message The furthest end date possible is 90 days due to the 3rd party only audience

                //- Status
                div.form-section(v-else-if="editedField === 'status'")
                    div.form-section--switch.l-flex-row--between
                        div.form-label.l-flex-row.flex-1
                            label.flex-1(for="switch-status") Status
                        div.onoffswitch
                            input(
                                id="switch-status"
                                name="switch-status"
                                class="onoffswitch-checkbox"
                                type="checkbox"
                                v-model="status"
                                true-value="active"
                                false-value="inactive"
                            )
                            label(class="onoffswitch-label" for="switch-status")
                    div.message-info
                        svg-icon(name='info' size="medium")
                        div.message {{`All the selected audiences will be set to ${status} upon saving`}}

                //- Bootstrap
                div.form-section(v-else-if="editedField === 'bootstrap'")
                    spinner.l-flex-row--center(v-if="bootstrapRetrieveIsLoading" width="50" height="50")
                    div.form-section--switch.l-flex-row--between(v-else :disabled="noBootstrapableAudience || bootstrapRetrieveIsLoading || dailyLimitBootstrapExceeded")
                        div.form-label.l-flex-row.flex-1
                            label.flex-1(for="switch-bootstrap") Bootstrap
                        div.onoffswitch
                            input(
                                id="switch-bootstrap"
                                name="switch-bootstrap"
                                class="onoffswitch-checkbox"
                                type="checkbox"
                                v-model="bootstrapStatus"
                                true-value="attribute"
                                false-value="none"
                            )
                            label(class="onoffswitch-label" for="switch-bootstrap")
                    div.message-info.form-validate-error.l-flex-row--center(v-if="noBootstrapableAudience || (unBootstrapableList.length > 0 && bootstrapStatus === 'attribute')" style="display:block")
                        div.l-flex-row--center
                            svg-icon.no-border(name='triangleExclamation' size="big")
                            div.message The following audiences cannot be bootstrapped. They may already have been bootstrapped{{bootstrapTextForRegular}}:
                        ul
                            li.message(v-for="aud in unBootstrapableList" :key="aud.id") {{ aud.id + ' - ' + aud.label}}
                    div.message-info.form-validate-error.l-flex-row--center(v-else-if="dailyLimitBootstrapExceeded")
                        div.message The daily bootstrap quota has been reached or would be reached with the current selection
                    div.message-info.l-flex-row--center(v-else-if="bootstrapStatus === 'attribute'")
                        svg-icon.no-border(name='info' size="medium")
                        div.message Selected audiences will be bootstraped

            //- Bulk delete
            .modal-body.modal-content-audience(v-else)
                div.message-info.form-validate-error.l-flex-row--center
                    div.l-flex-row--center
                        svg-icon.no-border(name='triangleExclamation' size="big")
                        div.message(style="font-size:12px") {{`You are about to permanently delete ${bulkList.length} audiences and the related transfers. Do you want to proceed?`}}

            //- FOOTER
            footer.modal-footer
                div.footer-content.l-flex-row--between
                    button.btn.btn--medium.btn--round(@click="closeModal()") Cancel
                    button.btn.btn--medium.btn--round.btn-rev.btn-save-audience(
                        @click="isDelete ? handleBulkDelete() : handleBulkEdit()"
                        type="button"
                        :class="{'ui-disabled': disabledButton || isUpdatedOrDeletedLoading}") {{ isDelete ? 'Delete' :'Edit' }}
</template>

<script>
import SectionHeader from '@/components/common/SectionHeader'
import dayjs from 'dayjs'
import Panel from '@/components/common/Panel'
import { mapActions, mapMutations, mapState, mapGetters } from 'vuex'
import { difference } from 'lodash'

export default {
    name: 'BulkEditForm',

    components: {
        SectionHeader,
        Panel
    },

    props: {
        bulkList: {
            type: Array,
            required: true
        },
        isDelete: {
            type: Boolean,
            required: false,
            default: false
        }
    },

    mounted() {
        const editableFields = [
            {
                label: 'Status',
                value: 'status'
            },
            {
                label: 'End Date',
                value: 'endDate'
            }
        ]
        if (this.userRole === 'webo_admin' || (this.bootstrapPush && (this.userRole !== 'webo_user' || this.getUserAccess({ type: 'monitoring_access' }) === 'write'))) {
            editableFields.push({
                label: 'Bootstrap',
                value: 'bootstrap'
            })
        }
        this.editableFields = editableFields
    },

    data() {
        return {
            editedField: {
                label: 'Status',
                value: 'status'
            },
            editableFields: [],
            endDate: dayjs(),
            status: 'inactive',
            bootstrapStatus: 'none',
            bootstrapRetrieveIsLoading: false,
            noBootstrapableAudience: false,
            dailyLimitBootstrapExceeded: false,
            bulkListForBootstrap: [],
            unBootstrapableList: []
        }
    },

    computed: {
        ...mapState('data/settings', {
            bootstrapPush: state => state.general.bootstrapPush
        }),
        ...mapState('data/audience', {
            isUpdatedOrDeletedLoading: state => state.isUpdatedOrDeletedLoading
        }),
        ...mapState('account', {
            userRole: state => state.userRole,
            dailyPushOfStockLimit: state => state.dailyPushOfStockLimit,
            attributionCounterAudience: state => state.attributionCounterAudience
        }),
        ...mapGetters('account', ['getUserAccess', 'getIsMonitoringActionAllowed']),

        disabledButton() {
            if (this.isDelete) return false

            if (this.editedField === 'endDate') {
                const date = dayjs().startOf('day')
                return dayjs(this.endDate).isBefore(date)
            } else if (this.editedField === 'status') {
                return false
            } else if (this.editedField === 'bootstrap') {
                return this.bootstrapStatus === 'none' || this.dailyLimitBootstrapExceeded
            }
            return true
        },

        hasThirdParty() {
            return this.bulkList.some(el => el.has_3rd_party && el.type === 'custom')
        },

        hasFirstParty() {
            return this.bulkList.some(el => el.has_1st_party)
        },

        endDateOptions() {
            return {
                firstDayOfWeek: 1,
                disabledDate(time) {
                    return (
                        dayjs()
                            .startOf('day')
                            .valueOf() >
                            dayjs(time)
                                .startOf('day')
                                .valueOf() ||
                        dayjs(time).valueOf() >=
                            dayjs()
                                .startOf('day')
                                .add(395, 'day')
                                .valueOf()
                    )
                }
            }
        },

        unBootstrapableMessage() {
            const unBootstrapableList = this.unBootstrapableList.map(el => {
                return `${el.id} - ${el.label} `
            })
            return `The following audiences cannot be bootstrapped. They may already have been bootstrapped${this.bootstrapTextForRegular}: \n ${unBootstrapableList.join(
                '<br>'
            )}`
        },
        bootstrapTextForRegular () {
            return this.userRole === 'webo_user' ? ' or contain 3rd party formula' : ''
        }
    },

    watch: {
        editedField: {
            handler: function(val) {
                if (val === 'bootstrap') this.launchBootstrapRequests()
            }
        },

        endDate(val, oldVal) {
            if (val === null || val === '') {
                this.endDate = oldVal ?? dayjs()
            }
        }
    },

    methods: {
        ...mapMutations('ui/monitoring', ['setDisplayModal']),
        ...mapActions('data/audience', [
            'bulkEditAudiences',
            'processAudienceStatistics',
            'bulkRetrieveAudiencesVolumes',
            'bulkDeleteAudiences',
        ]),
        ...mapActions('dashboardAccount', ['retrieveExpiringAudiences', 'retrieveExpiringTransfers']),

        async handleBulkEdit() {
            const list = this.editedField === 'bootstrap' ? this.bulkListForBootstrap : this.bulkList
            const audiences = list.map(el => el.id)
            const endDate = { end_date: dayjs(this.endDate).format('YYYY-MM-DD') }
            let fields
            if (this.editedField === 'endDate') fields = { ...endDate }
            else if (this.editedField === 'status') fields = { status: this.status }
            else if (this.editedField === 'bootstrap') fields = { bootstrap: 'attribute' }
            await this.bulkEditAudiences({ audiences, fields })
            this.retrieveExpiringAudiences()
            this.retrieveExpiringTransfers()
            this.$nextTick(() => {
                this.closeModal()
                this.$emit('bulkEditFinished')

            })
        },

        async handleBulkDelete() {
            await this.bulkDeleteAudiences(this.bulkList.map(el => el.id)).finally(() => {
                this.closeModal()
                this.$emit('bulkEditFinished')
                this.retrieveExpiringAudiences()
                this.retrieveExpiringTransfers()
            })
        },

        closeModal() {
            this.setDisplayModal({ item: 'bulkEdit', display: false })
        },

        async launchBootstrapRequests() {
            this.bootstrapRetrieveIsLoading = true
            let list = this.bulkList
            //Uncomment these 2 lines when api is ready to handle rebootstraping
            // if (this.userRole !== 'webo_admin') {
            list = this.bulkList.filter(el => el.bootstrap === 'none' && (el.type === 'cclones' || el.has_1st_party || this.userRole === 'webo_admin' || this.userRole === 'webo_employee'))
            this.unBootstrapableList = difference(this.bulkList, list)
            if (list.length < 1) {
                this.bootstrapRetrieveIsLoading = false
                this.noBootstrapableAudience = true
                return
            }
            // }

            // the shared audiences shouldn't be included in the quota check
            const totalVolumes = await this.bulkRetrieveAudiencesVolumes(list.filter(el => !el.isShared))
            if (this.userRole !== 'webo_admin' && this.attributionCounterAudience + totalVolumes > this.dailyPushOfStockLimit) {
                this.dailyLimitBootstrapExceeded = true
            }
            this.bulkListForBootstrap = list
            this.bootstrapRetrieveIsLoading = false
        }
    }
}
</script>

<style lang="scss">
.end-date__block {
    flex-direction: column !important;
    .form-subsection {
        min-height: auto;
        &:not(:first-child) {
            margin-left: 0;
            width: 100%;
            background: rgba(255, 103, 0, 0.1);
            border-radius: 4px;
            align-content: center;
            display: flex;
            margin-top: 10px;
            padding: 10px;
            .message-info {
                margin-top: 0;
                .message {
                    color: black;
                    margin-top: 0;
                }
            }
        }
    }
    svg {
        path {
            fill: rgba(255, 103, 0, 0.6);
        }
    }
}
</style>
